import { useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Search } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { PeriodPicker, REPORT_UNITS, usePeriod } from '@/components/ui/PeriodPicker';
import { Table } from '@/components/ui/Table';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/form/Input';
import { useToast } from '@/components/ui/useToast';
import { usePrompt } from '@/components/ui/usePrompt';
import { apiError } from '@/lib/apiError';
import { formatINR } from '@/lib/format';
import { cancelVoucher, dmy, getEditLog, getVoucher, listVouchers, type VoucherRow, type VoucherType } from '@/api/accountsBooks';
import { VoucherFormModal } from './VoucherFormModal';

const TITLE: Record<VoucherType, string> = { Receipt: 'Receipt', Payment: 'Payment', Contra: 'Contra', Journal: 'Journal', Sales: 'Sales', Purchase: 'Purchase', 'Credit Note': 'Credit Note', 'Debit Note': 'Debit Note' };

/** Who the voucher is with, and the cash / bank side, as each register shows it. */
function columnsFor(type: VoucherType) {
  const party = (r: VoucherRow) => (type === 'Receipt' ? r.credit : type === 'Payment' ? r.debit : []).join(', ');
  const acct = (r: VoucherRow) => r.cashBank.join(', ');
  if (type === 'Contra') return { first: 'From (Cr) → To (Dr)', firstCell: (r: VoucherRow) => `${r.credit.join(', ')} → ${r.debit.join(', ')}`, second: null };
  if (type === 'Journal') return { first: 'Debit', firstCell: (r: VoucherRow) => r.debit.join(', '), second: { header: 'Credit', cell: (r: VoucherRow) => r.credit.join(', ') } };
  return { first: type === 'Receipt' ? 'Received from' : 'Paid to', firstCell: party, second: { header: type === 'Receipt' ? 'Into' : 'From', cell: acct } };
}

/** Sales: Party · Taxable · GST. Purchase: Supplier · Supplier Bill · Taxable · GST (as the prototype's registers). */
function tradeColumns(type: VoucherType) {
  const gst = (r: VoucherRow) => (r.details ? r.details.cgst + r.details.sgst + r.details.igst : 0);
  const dash = (r: VoucherRow, v: ReactNode) => (r.status === 'Cancelled' || !r.details ? '—' : v);
  return [
    { key: 'party', header: type === 'Sales' ? 'Party' : 'Supplier', minWidth: '180px', cell: (r: VoucherRow) => dash(r, r.details?.party) },
    ...(type === 'Purchase' ? [{ key: 'bill', header: 'Supplier Bill', cell: (r: VoucherRow) => dash(r, <span className="font-mono">{r.details?.billNo}</span>) }] : []),
    { key: 'taxable', header: 'Taxable', align: 'right' as const, cell: (r: VoucherRow) => dash(r, `₹${formatINR(r.details?.taxable ?? 0)}`) },
    { key: 'gst', header: 'GST', align: 'right' as const, cell: (r: VoucherRow) => dash(r, `₹${formatINR(gst(r))}`) },
  ];
}

export function VoucherRegisterPage({ type }: { type: VoucherType }) {
  const period = usePeriod('month');
  const [search, setSearch] = useState('');
  const [form, setForm] = useState<{ id: number | null } | null>(null);
  const [viewId, setViewId] = useState<number | null>(null);
  const range = period.range;

  const q = useQuery({
    queryKey: ['acc-books', 'vouchers', type, range?.from ?? '', range?.to ?? ''],
    queryFn: () => listVouchers(type, { from: range?.from, to: range?.to }),
  });
  const rows = useMemo(() => {
    const s = search.trim().toLowerCase();
    const all = q.data ?? [];
    if (!s) return all;
    return all.filter((r) => [r.voucherNo, r.narration, r.refNo, ...r.debit, ...r.credit, ...r.against].some((x) => (x ?? '').toLowerCase().includes(s)));
  }, [q.data, search]);
  // Sales: the invoice total; Purchase: what is payable to the supplier; others: the voucher amount.
  const amountOf = (r: VoucherRow) => (type === 'Purchase' && r.details ? r.details.payable ?? r.amount : type === 'Sales' && r.details ? r.details.total : r.amount);
  const total = rows.filter((r) => r.status === 'Active').reduce((s, r) => s + amountOf(r), 0);
  const cols = columnsFor(type);
  const trade = type === 'Sales' || type === 'Purchase';

  const editQ = useQuery({ queryKey: ['acc-books', 'voucher', form?.id], queryFn: () => getVoucher(form!.id!), enabled: !!form?.id });

  return (
    <div>
      <SectionHeader title={TITLE[type]} />
      <FilterBar>
        <FilterField label="Search" className="col-span-2 sm:col-auto sm:min-w-[220px]">
          <div className="relative w-full">
            <Search size={11} className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] sm:left-2.5" strokeWidth={2.25} />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search" title="Voucher no., ledger, bill, reference or narration" className="!pl-5 sm:!pl-8" />
          </div>
        </FilterField>
        <FilterField label="Period" className="col-span-3 sm:col-auto">
          <PeriodPicker {...period.picker} units={REPORT_UNITS} className="justify-start" />
        </FilterField>
        <Button variant="primary" size="sm" className="col-span-3 sm:col-span-1 sm:ml-auto sm:self-end" icon={<Plus size={12} />} onClick={() => setForm({ id: null })}>
          New {type}
        </Button>
      </FilterBar>

      <Table<VoucherRow>
        storageKey={`acc-vouchers-${type}`}
        title={`${type} Register`}
        count={`${rows.length} voucher${rows.length === 1 ? '' : 's'} · ₹${formatINR(total)}`}
        rows={rows}
        rowKey={(r) => r.id}
        loading={q.isFetching && !q.data}
        empty={`No ${type.toLowerCase()} vouchers in this period.`}
        columns={[
          { key: 'no', header: type === 'Sales' ? 'Invoice No' : 'Voucher No', cell: (r) => <button type="button" onClick={() => setViewId(r.id)} className="font-mono font-semibold text-accent hover:underline">{r.voucherNo}</button> },
          { key: 'date', header: 'Date', cell: (r) => dmy(r.date) },
          { key: 'status', header: 'Status', cell: (r) => (r.status === 'Cancelled' ? <Badge tone="danger">Cancelled</Badge> : '—') },
          ...(trade ? tradeColumns(type) : [
            { key: 'first', header: cols.first, minWidth: '180px', cell: (r: VoucherRow) => (r.status === 'Cancelled' ? '—' : cols.firstCell(r)) },
            ...(cols.second ? [{ key: 'second', header: cols.second.header, cell: (r: VoucherRow) => (r.status === 'Cancelled' ? '—' : cols.second!.cell(r)) }] : []),
            ...(type === 'Journal' ? [
              { key: 'kind', header: 'Type', cell: (r: VoucherRow) => (r.details?.kind === 'exp' ? <Badge tone="info">Expense bill</Badge> : 'General') },
              { key: 'head', header: 'Expense Head', cell: (r: VoucherRow) => (r.details?.kind === 'exp' ? r.details.head : '—') },
            ] : [{ key: 'mode', header: 'Mode / Ref', cell: (r: VoucherRow) => [r.mode, r.refNo].filter(Boolean).join(' · ') || '—' }]),
            { key: 'against', header: 'Against', cell: (r: VoucherRow) => r.against.join(', ') || '—' },
          ]),
          { key: 'narr', header: 'Narration', minWidth: '160px', cell: (r) => <span className="text-[var(--text-muted)]">{r.narration || '—'}</span> },
          { key: 'amt', header: type === 'Sales' ? 'Invoice Total' : type === 'Purchase' ? 'Payable' : 'Amount', align: 'right', cell: (r) => <strong>₹{formatINR(amountOf(r))}</strong> },
        ]}
      />

      {viewId != null && <VoucherView id={viewId} onClose={() => setViewId(null)} onAlter={() => { setForm({ id: viewId }); setViewId(null); }} />}
      {form && (!form.id || editQ.data) && (
        <VoucherFormModal type={type} voucher={form.id ? editQ.data! : null} onClose={() => setForm(null)} />
      )}
    </div>
  );
}

/** A voucher's lines and bills, its history, and Alter / Cancel. */
/** A voucher. Opened from a report (no onAlter) it is read-only: no Alter, no Cancel. */
export function VoucherView({ id, onClose, onAlter }: { id: number; onClose: () => void; onAlter?: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const ask = usePrompt();
  const q = useQuery({ queryKey: ['acc-books', 'voucher', id], queryFn: () => getVoucher(id) });
  const logQ = useQuery({ queryKey: ['acc-books', 'edit-log', 'voucher', id], queryFn: () => getEditLog({ voucherId: id }) });
  const v = q.data;
  const cancel = useMutation({
    mutationFn: (reason: string) => cancelVoucher(id, reason),
    onSuccess: (x) => { toast.push(`${x.voucherNo} cancelled`, 'success'); qc.invalidateQueries({ queryKey: ['acc-books'] }); },
    onError: (e) => toast.push(apiError(e, 'Cancel failed'), 'error'),
  });

  const doCancel = async () => {
    if (!v) return;
    const reason = await ask({
      title: `Cancel ${v.voucherNo}?`,
      message: 'The voucher keeps its number but its amounts come off the books. This can’t be undone.',
      label: 'Reason', placeholder: 'Optional', required: false, tone: 'danger', confirmLabel: 'Cancel voucher',
    });
    if (reason !== null) cancel.mutate(reason);
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      title={v ? `${v.type} ${v.voucherNo}` : 'Voucher'}
      footer={v && (
        <div className="flex w-full justify-end gap-2">
          <Button variant="glass" onClick={onClose}>Close</Button>
          {v.status === 'Active' && onAlter && <Button variant="danger" disabled={cancel.isPending} onClick={doCancel}>Cancel voucher</Button>}
          {v.status === 'Active' && onAlter && <Button variant="primary" onClick={onAlter}>Alter</Button>}
        </div>
      )}
    >
      {!v ? <p className="text-sm text-[var(--text-muted)]">Loading…</p> : (
        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
            <Info label="Date" value={dmy(v.date)} />
            <Info label="Status" value={v.status} />
            {v.mode && <Info label="Mode" value={v.mode} />}
            {v.refNo && <Info label="Ref no." value={v.refNo} />}
            {v.instrDate && <Info label="Instrument date" value={dmy(v.instrDate)} />}
            {v.bankName && <Info label="Cheque drawn on" value={v.bankName} />}
            {v.favouring && <Info label="Favouring" value={v.favouring} />}
            {v.details && (v.type === 'Sales' || v.type === 'Purchase') && <Info label={v.type === 'Sales' ? 'Customer GSTIN' : 'Supplier GSTIN'} value={v.details.gstin ?? 'Unregistered'} />}
            {v.details?.billDate && <Info label="Supplier bill date" value={dmy(v.details.billDate)} />}
            {v.details?.againstBill && <Info label={v.type === 'Debit Note' ? 'Against bill' : 'Against invoice'} value={v.details.againstBill} />}
            {v.details?.reason && <Info label="Reason" value={v.details.reason} />}
            {v.details?.irn && <Info label="IRN" value={`${v.details.irn.slice(0, 12)}…`} />}
            {v.details?.ewayBillNo && <Info label="e-Way bill" value={v.details.ewayBillNo} />}
            {v.details?.vehicle && <Info label="Vehicle" value={`${v.details.vehicle} · ${v.details.km ?? 0} km`} />}
            {v.details?.kind === 'exp' && !!v.details.tds && <Info label={`TDS ${v.details.tdsSection ?? ''}`} value={`₹${formatINR(v.details.tds)}`} />}
            {!!v.details?.tdsAmount &&<Info label="TDS 1031" value={`₹${formatINR(v.details.tdsAmount)}`} />}
            <Info label="Entered by" value={v.createdByName ?? '—'} />
          </div>
          {v.items?.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full whitespace-nowrap text-xs">
                <thead className="text-[var(--text-muted)]">
                  <tr><th className="py-1 text-left">Item</th><th className="py-1 text-left">HSN</th><th className="py-1 text-right">Qty</th><th className="py-1 text-right">Rate</th><th className="py-1 text-right">Disc %</th><th className="py-1 text-right">GST</th><th className="py-1 text-right">Amount</th></tr>
                </thead>
                <tbody>
                  {v.items.map((i, k) => (
                    <tr key={k} className="border-t border-[var(--border-color)]">
                      <td className="py-1 pr-2">{i.name}</td><td className="py-1 pr-2 font-mono">{i.hsn}</td><td className="py-1 text-right">{i.qty} {i.unit}</td>
                      <td className="py-1 text-right">₹{formatINR(i.rate)}</td><td className="py-1 text-right">{i.disc || ''}</td><td className="py-1 text-right">{i.gst}%</td><td className="py-1 text-right">₹{formatINR(i.amount ?? 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {v.lines.length > 0 && (
            <table className="w-full text-xs">
              <thead className="text-[var(--text-muted)]">
                <tr><th className="py-1 text-left">Particulars</th><th className="py-1 text-right">Debit</th><th className="py-1 text-right">Credit</th></tr>
              </thead>
              <tbody>
                {v.lines.map((l) => (
                  <tr key={l.ledgerId} className="border-t border-[var(--border-color)] align-top">
                    <td className="py-1.5">
                      <div className="font-semibold">{l.ledger}</div>
                      {l.bills.map((b, i) => (
                        <div key={i} className="text-[var(--text-muted)]">{b.refType}{b.billNo ? ` ${b.billNo}` : ''} · ₹{formatINR(b.amount)}{b.dueDate ? ` · due ${dmy(b.dueDate)}` : ''}</div>
                      ))}
                    </td>
                    <td className="py-1.5 text-right">{l.side === 'Dr' ? `₹${formatINR(l.amount)}` : ''}</td>
                    <td className="py-1.5 text-right">{l.side === 'Cr' ? `₹${formatINR(l.amount)}` : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {v.narration && <p className="text-xs text-[var(--text-secondary)]"><strong>Narration:</strong> {v.narration}</p>}
          <div>
            <div className="mb-1 text-xs font-semibold text-[var(--text-secondary)]">History</div>
            {(logQ.data ?? []).map((l) => (
              <div key={l.id} className="text-xs text-[var(--text-muted)]">
                v{l.version} · {l.action} · {new Date(l.at).toLocaleString('en-IN')} · {l.by ?? '—'} · ₹{formatINR(l.snapshot.amount)}
              </div>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">{label}</div>
      <div className="font-semibold text-[var(--text-primary)]">{value}</div>
    </div>
  );
}

export const ReceiptPage = () => <VoucherRegisterPage type="Receipt" />;
export const PaymentPage = () => <VoucherRegisterPage type="Payment" />;
export const ContraPage = () => <VoucherRegisterPage type="Contra" />;
export const JournalPage = () => <VoucherRegisterPage type="Journal" />;
export const SalesVoucherPage = () => <VoucherRegisterPage type="Sales" />;
export const PurchaseVoucherPage = () => <VoucherRegisterPage type="Purchase" />;
